import { ApiErrorResponseSchema } from "@contextos/shared";
import type { z } from "zod";
import { env } from "@/config/env";

/** Error thrown for any non-2xx API response, carrying the server's error envelope. */
export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiClientError";
  }

  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

type QueryValue = string | number | boolean | null | undefined;

/** Throw the server's error envelope (or a generic error) for a non-2xx response. */
export async function throwApiError(response: Response): Promise<never> {
  const json: unknown = await response.json().catch(() => null);
  const parsed = ApiErrorResponseSchema.safeParse(json);
  if (parsed.success) {
    const { code, message, details, requestId } = parsed.data.error;
    throw new ApiClientError(response.status, code, message, details, requestId);
  }
  throw new ApiClientError(
    response.status,
    "HTTP_ERROR",
    `Request failed with status ${response.status}`,
  );
}

export interface ApiRequestOptions<TResponse> {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, QueryValue>;
  /** Zod schema used to validate the response at the boundary. */
  schema: z.ZodType<TResponse>;
  signal?: AbortSignal;
}

/** Absolute API URL for a path under the configured base, with optional query string. */
export function buildApiUrl(path: string, query?: Record<string, QueryValue>): string {
  const base = env.VITE_API_BASE_URL.replace(/\/+$/, "");
  const url = `${base}/${path.replace(/^\/+/, "")}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Minimal typed fetch wrapper. Every response is validated against a schema
 * from `@contextos/shared`, so the UI never trusts unvalidated JSON.
 */
export async function apiRequest<TResponse>(
  path: string,
  { method = "GET", body, query, schema, signal }: ApiRequestOptions<TResponse>,
): Promise<TResponse> {
  const response = await fetch(buildApiUrl(path, query), {
    method,
    // The session is an HttpOnly cookie on this origin (`/api` is proxied same-origin).
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (response.status === 204) {
    return schema.parse(undefined);
  }

  if (!response.ok) await throwApiError(response);

  const json: unknown = await response.json().catch(() => null);
  return schema.parse(json);
}

/**
 * Downloads a file the API serves as an attachment (an export) and has the browser save it under
 * the name the API gives, or `fallbackName`.
 */
export async function apiDownload(
  path: string,
  { query, fallbackName }: { query?: Record<string, QueryValue>; fallbackName: string },
): Promise<string> {
  const response = await fetch(buildApiUrl(path, query), { headers: { Accept: "*/*" } });
  if (!response.ok) await throwApiError(response);
  const disposition = response.headers.get("content-disposition") ?? "";
  const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return name;
}
